import { useState } from "react";
import { toast } from "sonner";
import { Copy, Eye, Globe } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, Spinner } from "@/components/Common";
import { PublicCard } from "@/components/PublicCard";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const VIS = [["bio", "Elevator pitch"], ["current", "Current role & employer"], ["location", "Location & target states"], ["skills", "Skills, stack & certifications"], ["target_roles", "Target roles"], ["links", "LinkedIn, GitHub, website"]];
const DEFAULT_VIS = Object.fromEntries(VIS.map(([k]) => [k, true]));
const SLUG_KEY = "rpopro_public_slug";
const VIS_KEY = "rpopro_public_vis";

const slugFrom = (d) => d?.slug || String(d?.url || d?.path || "").split("/").filter(Boolean).pop() || "";

export const applyVisibility = (src, vis) => ({
  name: src.name, picture: src.picture, headline: src.headline, years_experience: src.years_experience, career_level: src.career_level,
  work_models: src.work_models, open_to_relocation: src.open_to_relocation,
  ...(vis.bio && { bio: src.bio }),
  ...(vis.current && { current_title: src.current_title, current_employer: src.current_employer }),
  ...(vis.location && { location: src.location, preferred_states: src.preferred_states }),
  ...(vis.skills && { skills: src.skills, tech_stack: src.tech_stack, soft_skills: src.soft_skills, certifications: src.certifications }),
  ...(vis.target_roles && { target_roles: src.target_roles }),
  ...(vis.links && { linkedin_url: src.linkedin_url, github_url: src.github_url, portfolio_url: src.portfolio_url }),
});

export const PublicProfileSection = ({ f, dirty }) => {
  const { user } = useAuth();
  const [vis, setVis] = useState(() => ({ ...DEFAULT_VIS, ...JSON.parse(localStorage.getItem(VIS_KEY) || "{}") }));
  const [slug, setSlug] = useState(() => localStorage.getItem(SLUG_KEY) || "");
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const data = applyVisibility({ ...f, name: user?.name, picture: user?.picture }, vis);
  const link = slug ? `${window.location.origin}/u/${slug}` : "";

  const toggle = (k, v) => { const next = { ...vis, [k]: v }; setVis(next); localStorage.setItem(VIS_KEY, JSON.stringify(next)); };
  const publish = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/portfolio/publish", { visibility: vis });
      const s = slugFrom(d);
      if (!s) throw new Error("Publish didn't return a link");
      setSlug(s); localStorage.setItem(SLUG_KEY, s);
      navigator.clipboard?.writeText(`${window.location.origin}/u/${s}`).catch(() => {});
      toast.success("Published. Link copied.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Card id="public" className="scroll-mt-24" data-testid="public-profile-section">
      <div className="mb-1 flex items-center gap-2"><Globe className="h-4 w-4 text-[#D4AF37]" /><h3 className="font-display text-lg text-zinc-100">Public profile card</h3></div>
      <p className="mb-4 text-xs text-zinc-500">A polished page to share with recruiters. Salary, clearance, authorization and email are never shown.</p>
      <div className="space-y-2.5">
        {VIS.map(([k, l]) => (
          <label key={k} className="flex items-center justify-between text-sm text-zinc-300">{l}<Switch checked={!!vis[k]} onCheckedChange={(v) => toggle(k, v)} data-testid={`public-vis-${k}`} /></label>
        ))}
      </div>
      <div className="mt-5 flex gap-2">
        <Button variant="outline" className="flex-1" onClick={() => setPreview(true)} data-testid="public-preview-button"><Eye className="h-4 w-4" />Preview</Button>
        <Button className="flex-1" onClick={publish} disabled={busy || dirty} data-testid="public-publish-button">{busy ? <Spinner /> : slug ? "Republish" : "Publish"}</Button>
      </div>
      {dirty && <p className="mt-2 text-[11px] text-zinc-500" data-testid="public-save-first">Save your profile first, then publish.</p>}
      {link && (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-[#27272A] bg-[#09090B] p-2">
          <a href={link} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate font-mono text-xs text-[#F3C649]" data-testid="public-profile-link">{link}</a>
          <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }} className="text-zinc-500 hover:text-[#D4AF37]" data-testid="public-copy-link"><Copy className="h-4 w-4" /></button>
        </div>
      )}
      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-h-[calc(100dvh-1.5rem)] sm:max-h-[92vh] overflow-y-auto rounded-3xl border-0 bg-[#09090B] p-0 shadow-none sm:max-w-3xl" data-testid="public-preview-dialog">
          <DialogTitle className="sr-only">Public profile preview</DialogTitle>
          <PublicCard d={data} />
        </DialogContent>
      </Dialog>
    </Card>
  );
};
